/**
 * @overview convenience HOC for wrapping a component with an error boundary
 * This is sometimes nicer than declaratively inserting an <ErrorBoundary> around a component's mount site
 * because you can contain all of the error handling rendering and tracking co-located with the component being wrapped
 */

import React, { ReactNode } from 'react'
import ErrorBoundary from 'components/ErrorBoundary'
import getDisplayName from 'lib/get-display-name'

interface ErrorBoundaryOptions {
  errorComponent?: ReactNode
  onError?: (error: Error, errorInfo: object) => void
}

export default function withErrorBoundary({
  errorComponent,
  onError
}: ErrorBoundaryOptions = {}) {
  return <P extends object>(Component: React.ComponentType<P>) => {
    return class WithErrorBoundary extends React.Component<P> {
      static displayName = `WithErrorBoundary(${getDisplayName(Component)})`

      render() {
        return (
          <ErrorBoundary errorComponent={errorComponent} onError={onError}>
            <Component {...this.props as P} />
          </ErrorBoundary>
        )
      }
    }
  }
}
